import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users, Calendar, ArrowLeft, Mail, Phone, Trash2, Download, Loader2 } from 'lucide-react';
import apiClient from '@/services/api/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/shared/PageHeader';
import { toast } from 'sonner';

type Registrant = {
  id: string;
  name: string;
  email: string;
  firm: string | null;
  phone: string | null;
  notes: string | null;
  created_at: string;
};

type Response = {
  workshop: { id: string; slug: string; title: string; startsAt: string; status: string };
  registrants: Registrant[];
};

export default function WorkshopRegistrantsPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['workshop-registrants', id],
    queryFn: async () => {
      const r = await apiClient.get<{ data: Response }>(`/admin/workshops/${id}/registrants`);
      return r.data.data;
    },
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: async (rId: string) => apiClient.delete(`/admin/workshops/${id}/registrants/${rId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workshop-registrants', id] });
      qc.invalidateQueries({ queryKey: ['admin-workshops'] });
      toast.success('Registrant removed');
    },
  });

  const exportCsv = () => {
    if (!data) return;
    const rows = [
      ['Name', 'Email', 'Firm', 'Phone', 'Notes', 'Registered'],
      ...data.registrants.map(r => [
        r.name, r.email, r.firm || '', r.phone || '', (r.notes || '').replace(/\n/g, ' '),
        new Date(r.created_at).toISOString(),
      ]),
    ];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${data.workshop.slug}-registrants.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <Link to="/panel/admin/workshops" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to workshops
      </Link>

      <PageHeader
        icon={Users}
        title={data?.workshop.title || 'Registrants'}
        description={
          data ? (
            <span className="inline-flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                {new Date(data.workshop.startsAt).toLocaleString('en-AU', {
                  weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
                  hour: 'numeric', minute: '2-digit', hour12: true,
                })}
              </span>
              <Badge variant="outline" className="text-[10px]">{data.workshop.status}</Badge>
            </span>
          ) : 'Loading…'
        }
      />

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : !data || data.registrants.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Users className="h-10 w-10 text-muted-foreground/50" />
            <p className="mt-4 text-sm font-medium">No registrants yet</p>
            <p className="mt-1 text-xs text-muted-foreground">Share the workshop link to start getting sign-ups.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{data.registrants.length}</span> {data.registrants.length === 1 ? 'registrant' : 'registrants'}
            </p>
            <Button size="sm" variant="outline" onClick={exportCsv}>
              <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-muted/40 border-b border-border">
                  <tr>
                    <th className="text-left px-4 py-2.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Name · Email</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Firm</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Phone</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Registered</th>
                    <th className="px-4 py-2.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.registrants.map(r => (
                    <tr key={r.id} className="hover:bg-muted/20">
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold">{r.name}</p>
                        <a href={`mailto:${r.email}`} className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1 mt-0.5">
                          <Mail className="h-3 w-3" /> {r.email}
                        </a>
                        {r.notes && (
                          <p className="text-xs text-muted-foreground italic mt-1.5 line-clamp-2">"{r.notes}"</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{r.firm || '—'}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {r.phone ? (
                          <a href={`tel:${r.phone}`} className="hover:text-primary inline-flex items-center gap-1">
                            <Phone className="h-3 w-3" /> {r.phone}
                          </a>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {new Date(r.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            if (confirm(`Remove ${r.name} (${r.email}) from this workshop?`)) {
                              deleteMutation.mutate(r.id);
                            }
                          }}
                          className="text-rose-600 hover:text-rose-700"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
